/**
 * useLaunchActivation — fetch-on-mount + refetch + setIntent (contracts §4).
 * Router-free: the caller passes projectId. No polling; the endpoint is no-store.
 *
 * AM-2 (binding controller ruling): the initial mount fetch is deduped
 * per-projectId via a module-level in-flight-promise latch, so mounting two
 * consumers for the same project (sidebar short form + nav badge) costs one
 * network read. An explicit `refetch()` always issues a fresh request — the
 * latch only protects the mount race, never a deliberate re-fetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActivationIntent, ActivationProjection } from '@modules/shared-types';
import { LaunchActivationApiError, launchActivationClient } from '../api/launchActivationClient';

export interface UseLaunchActivationResult {
  data?: ActivationProjection;
  loading: boolean;
  error?: string;
  /** Additive: HTTP status of the last failure (403/404 → AC-058 handling in the page). */
  status?: number;
  refetch: () => Promise<void>;
  setIntent: (intent: Omit<ActivationIntent, 'updatedAt'>) => Promise<void>;
}

/** Module-level latch (AM-2): in-flight `getActivation` promise per projectId. */
const inFlightGetByProjectId = new Map<string, Promise<ActivationProjection>>();

function dedupedGetActivation(projectId: string): Promise<ActivationProjection> {
  const existing = inFlightGetByProjectId.get(projectId);
  if (existing) return existing;
  const promise = launchActivationClient.getActivation(projectId);
  inFlightGetByProjectId.set(projectId, promise);
  // `.finally()` returns a derived promise that re-rejects on failure; the
  // caller (runFetch) already awaits and catches `promise` itself, so the
  // derived chain needs its own no-op `.catch` or Node reports an unhandled
  // rejection for it.
  promise
    .finally(() => {
      if (inFlightGetByProjectId.get(projectId) === promise) {
        inFlightGetByProjectId.delete(projectId);
      }
    })
    .catch(() => { /* handled by the caller awaiting `promise` */ });
  return promise;
}

export function useLaunchActivation(projectId: string | undefined): UseLaunchActivationResult {
  const [data, setData] = useState<ActivationProjection | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId));
  const [error, setError] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<number | undefined>(undefined);
  const requestSeq = useRef(0);

  const runFetch = useCallback(async (fetcher: (id: string) => Promise<ActivationProjection>) => {
    if (!projectId) { setLoading(false); return; }
    const seq = ++requestSeq.current;
    setError(undefined);
    setStatus(undefined);
    try {
      const fresh = await fetcher(projectId);
      if (seq !== requestSeq.current) return;
      setData(fresh);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus(err instanceof LaunchActivationApiError ? err.status : undefined);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [projectId]);

  const refetch = useCallback(async () => {
    await runFetch(launchActivationClient.getActivation);
  }, [runFetch]);

  const setIntent = useCallback(async (intent: Omit<ActivationIntent, 'updatedAt'>) => {
    if (!projectId) return;
    const seq = ++requestSeq.current;
    setError(undefined);
    try {
      const fresh = await launchActivationClient.setIntent(projectId, intent);
      if (seq !== requestSeq.current) return;
      setData(fresh);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus(err instanceof LaunchActivationApiError ? err.status : undefined);
      throw err;
    }
  }, [projectId]);

  useEffect(() => {
    requestSeq.current += 1;
    setData(undefined);
    setLoading(Boolean(projectId));
    setError(undefined);
    setStatus(undefined);
    if (projectId) {
      void runFetch(dedupedGetActivation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return { data, loading, error, status, refetch, setIntent };
}
